import { useEffect, useRef, useState } from "react";
import { X, Check, Loader2, ZoomIn } from "lucide-react";
import { api } from "@/lib/api";

const VIEW = 280;
const OUT = 640;

// WhatsApp-style circular crop: drag to move, slider to zoom, exports 640x640 JPEG.
export default function AvatarCropper({ file, onDone, onCancel }) {
    const [img, setImg] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [pos, setPos] = useState({ x: 0, y: 0 });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const drag = useRef(null);

    useEffect(() => {
        const url = URL.createObjectURL(file);
        const i = new Image();
        i.onload = () => setImg(i);
        i.src = url;
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const base = img ? VIEW / Math.min(img.width, img.height) : 1;
    const scale = base * zoom;
    const w = img ? img.width * scale : 0;
    const h = img ? img.height * scale : 0;
    const clamp = (p) => ({
        x: Math.min(0, Math.max(VIEW - w, p.x)),
        y: Math.min(0, Math.max(VIEW - h, p.y)),
    });

    useEffect(() => {
        if (img) setPos(clamp({ x: (VIEW - w) / 2, y: (VIEW - h) / 2 }));
    }, [img]); // eslint-disable-line react-hooks/exhaustive-deps

    const onZoom = (z) => {
        const ns = base * z;
        const cx = (VIEW / 2 - pos.x) / scale;
        const cy = (VIEW / 2 - pos.y) / scale;
        setZoom(z);
        const nw = img.width * ns, nh = img.height * ns;
        setPos({
            x: Math.min(0, Math.max(VIEW - nw, VIEW / 2 - cx * ns)),
            y: Math.min(0, Math.max(VIEW - nh, VIEW / 2 - cy * ns)),
        });
    };

    const down = (e) => {
        drag.current = { sx: e.clientX, sy: e.clientY, px: pos.x, py: pos.y };
        e.currentTarget.setPointerCapture(e.pointerId);
    };
    const move = (e) => {
        if (!drag.current) return;
        const d = drag.current;
        setPos(clamp({ x: d.px + e.clientX - d.sx, y: d.py + e.clientY - d.sy }));
    };

    const save = async () => {
        setBusy(true);
        setErr("");
        try {
            const c = document.createElement("canvas");
            c.width = OUT;
            c.height = OUT;
            const ctx = c.getContext("2d");
            ctx.fillStyle = "#fff";
            ctx.fillRect(0, 0, OUT, OUT);
            const k = OUT / VIEW;
            ctx.drawImage(img, pos.x * k, pos.y * k, w * k, h * k);
            const blob = await new Promise((r) => c.toBlob(r, "image/jpeg", 0.9));
            const form = new FormData();
            form.append("file", blob, "customer.jpg");
            const { data } = await api.post("/admin/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
            onDone(data.url);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Ngarkimi dështoi");
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/60 z-[60] flex items-center justify-center p-4" data-testid="avatar-cropper">
            <div className="bg-white border border-slate-200 rounded-xl w-full max-w-sm p-5">
                <div className="flex items-center justify-between mb-4">
                    <div className="font-display font-black uppercase">Prit foton</div>
                    <button onClick={onCancel} data-testid="avatar-cropper-cancel"><X className="w-4 h-4" /></button>
                </div>
                <div
                    className="relative mx-auto overflow-hidden rounded-lg bg-slate-900 touch-none cursor-grab active:cursor-grabbing"
                    style={{ width: VIEW, height: VIEW }}
                    onPointerDown={down}
                    onPointerMove={move}
                    onPointerUp={() => (drag.current = null)}
                    data-testid="avatar-cropper-stage"
                >
                    {img && <img src={img.src} alt="" draggable={false} className="absolute max-w-none select-none" style={{ left: pos.x, top: pos.y, width: w, height: h }} />}
                    <div className="absolute inset-0 pointer-events-none rounded-full" style={{ boxShadow: "0 0 0 999px rgba(0,0,0,0.55)" }} />
                    <div className="absolute inset-0 pointer-events-none rounded-full border-2 border-white/80" />
                </div>
                <label className="flex items-center gap-3 mt-4">
                    <ZoomIn className="w-4 h-4 text-slate-500" />
                    <input type="range" min="1" max="4" step="0.01" value={zoom} disabled={!img} onChange={(e) => onZoom(+e.target.value)} className="flex-1" data-testid="avatar-cropper-zoom" />
                </label>
                <p className="text-[11px] font-mono text-slate-500 mt-1">Tërhiqni foton për ta lëvizur · 640×640</p>
                {err && <div className="text-xs text-red-600 mt-2">{err}</div>}
                <div className="flex justify-end gap-2 mt-4">
                    <button onClick={onCancel} className="btn-outline">Anulo</button>
                    <button onClick={save} disabled={!img || busy} className="btn-primary" data-testid="avatar-cropper-save">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Ruaj
                    </button>
                </div>
            </div>
        </div>
    );
}
